import { action, computed } from 'mobx';
import { Entity } from '@/engine/core/entity';
import type { IValue } from '@/engine/DataFlow/Graph';
import { Type } from '@/engine/DataFlow/Type';
import type { IIndicationModel } from '../interfaces';
import type * as THREE from 'three';

export interface AngleProps {
  from?: THREE.Vector3;
  to?: THREE.Vector3;
  corner?: THREE.Vector3;
  angleDeg?: number;
}

export class AngleModel extends Entity<AngleProps> implements IIndicationModel, IValue {
  public static readonly Type: Type = new Type('AngleModel');

  public readonly type: Type = AngleModel.Type;

  private _updateAngle(): void {
    if (!this.props.from || !this.props.to || !this.props.corner) {
      this.props.angleDeg = undefined;
    } else {
      this.props.angleDeg =
        (this.props.from.clone().sub(this.props.corner).angleTo(this.props.to.clone().sub(this.props.corner)) * 180) /
        Math.PI;
    }
  }

  public getPoints(): Array<THREE.Vector3 | undefined> {
    return [this.props.from, this.props.corner, this.props.to];
  }

  public getPointByIndex(index: number): THREE.Vector3 | undefined {
    const keys = ['from', 'corner', 'to'] as const;
    const key = keys[index];
    return this.props[key];
  }

  @action
  public setPointByIndex(index: number, point: THREE.Vector3): boolean {
    if (index > 2) return false;
    const keys = ['from', 'corner', 'to'] as const;
    const key = keys[index];
    this.props[key] = point;
    this._updateAngle();
    return true;
  }

  public get from(): THREE.Vector3 | undefined {
    return this.props.from;
  }

  @action
  public setFromPoint(point: THREE.Vector3 | undefined): void {
    this.props.from = point?.clone();
    this._updateAngle();
  }

  public get to(): THREE.Vector3 | undefined {
    return this.props.to;
  }

  @action
  public setToPoint(point: THREE.Vector3 | undefined): void {
    this.props.to = point?.clone();
    this._updateAngle();
  }

  public get corner(): THREE.Vector3 | undefined {
    return this.props.corner;
  }

  @action
  public setCornerPoint(point: THREE.Vector3 | undefined): void {
    this.props.corner = point?.clone();
    this._updateAngle();
  }

  @computed
  public get angleDeg(): number | undefined {
    return this.props.angleDeg;
  }
}
